import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import cormorantLatin from "@/assets/fonts-cormorant-garamond-400-normal-latin.woff2.asset.json";
import montserratLatin from "@/assets/fonts-montserrat-400-normal-latin.woff2.asset.json";
import cormorantItalic from "@/assets/fonts-cormorant-garamond-400-italic-latin.woff2.asset.json";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader, SiteFooter } from "../components/site-chrome";
import { DeferredChrome } from "../components/deferred-mount";
import { CookieConsent } from "@/components/cookie-consent";
import { GSC_VERIFICATION } from "@/config/analytics";
import { useAnalyticsPageView } from "../hooks/use-analytics-page-view";
import { useScrollDepth } from "../hooks/use-scroll-depth";
import { ORGANIZATION_LOGO, ORGANIZATION_SCHEMA } from "@/lib/schema-org";

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=DJ+Val+Cavaillon&query_place_id=ChIJn2-Ifaz3tRIRQGJB4UPeKaw";





function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "DJ Val" },
      { name: "theme-color", content: "#050B18" },
      { property: "og:site_name", content: "DJ Val" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "fr_FR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@djval" },
      { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" },
      { name: "format-detection", content: "telephone=yes" },
      { name: "geo.region", content: "FR-84" },
      { name: "geo.placename", content: "Cavaillon, Vaucluse" },
      { name: "geo.position", content: "43.8358;5.0378" },
      { name: "ICBM", content: "43.8358, 5.0378" },
      // Verification Google Search Console : renseignez GSC_VERIFICATION dans src/config/analytics.ts
      ...(GSC_VERIFICATION
        ? [{ name: "google-site-verification", content: GSC_VERIFICATION }]
        : []),
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      // Aucun preconnect tiers : toutes les images et logos sont désormais
      // servis en local, et les vidéos YouTube ne sont chargées qu'au clic.
      { rel: "preload", as: "font", type: "font/woff2", href: cormorantLatin.url, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: cormorantItalic.url, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: montserratLatin.url, crossOrigin: "anonymous" },

    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["LocalBusiness", "MusicGroup"],
              "@id": "https://dj-val.fr/#business",
              name: "DJ Val",
              alternateName: "DJ Val Provence",
              description:
                "DJ professionnel pour mariages, soirées privées et événements d'entreprise en Provence, Vaucluse, Luberon & Alpilles. 20 ans d'expérience, régie Pioneer DJ (CDJ-3000, DJM-A9, XDJ-RX3, Opus Quad), sonorisation RCF, éclairage LED Chauvet.",
              url: "https://dj-val.fr",
              image: "https://dj-val.fr/og-dj-val.jpg",
              logo: ORGANIZATION_LOGO,
              telephone: "+33767908667",
              email: "info.djval@gmail.com",
              priceRange: "€€",
              foundingDate: "2005",
              slogan: "L'émotion d'une soirée inoubliable",
              founder: {
                "@type": "Person",
                name: "Stéphane Orcet",
                alternateName: "DJ Val",
                jobTitle: "DJ professionnel · 20 ans d'expérience",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "Cavaillon",
                  addressRegion: "Vaucluse",
                  addressCountry: "FR",
                },
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "106 Grand Rue",
                addressLocality: "Cavaillon",
                postalCode: "84300",
                addressRegion: "Vaucluse",
                addressCountry: "FR",
              },
              geo: {
                "@type": "GeoCoordinates",
                latitude: 43.8373156,
                longitude: 5.03688,
              },
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                  opens: "09:00",
                  closes: "22:00",
                },
              ],
              areaServed: [
                "Vaucluse", "Luberon", "Alpilles", "Provence", "Comtat Venaissin",
                "Avignon", "Cavaillon", "Aix-en-Provence", "Arles", "Nîmes",
                "Orange", "Carpentras", "Saint-Rémy-de-Provence",
                "Salon-de-Provence", "Pertuis", "Apt", "Uzès",
                "Gordes", "Lourmarin", "Ménerbes", "Bonnieux", "Roussillon",
              ].map((name) => ({ "@type": "City", name })),
              hasMap: GOOGLE_MAPS_URL,
              sameAs: [
                GOOGLE_MAPS_URL,
                "https://www.mariages.net/musique-mariage/dj-val--e377799",
                "https://www.facebook.com/djval84",
                "https://www.instagram.com/djval_provence",
                "https://www.youtube.com/@djval-provence",
              ],
            },
            {
              "@type": "Person",
              "@id": "https://dj-val.fr/#val",
              name: "Val",
              jobTitle: "DJ professionnel",
              worksFor: { "@id": "https://dj-val.fr/#business" },
              knowsAbout: [
                "Animation musicale mariage",
                "Mix DJ",
                "Sonorisation événementielle",
                "Éclairage scénique",
              ],
            },
            ORGANIZATION_SCHEMA,
            {
              "@type": "WebSite",
              "@id": "https://dj-val.fr/#website",
              url: "https://dj-val.fr",
              name: "DJ Val · DJ Mariage & Événements Provence",
              inLanguage: "fr-FR",
              publisher: { "@id": "https://dj-val.fr/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = pathname === "/en" || pathname.startsWith("/en/") ? "en" : "fr";
  return (
    <html lang={lang}>
      <head>
        <HeadContent />
        {/* Ahrefs Analytics — tracking site-wide pour SEO et visiteurs */}
        <script
          src="https://analytics.ahrefs.com/analytics.js"
          data-key="DbGqrypCGar+X6MG0DTyag"
          async
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.searchStr });

  const fullPath = pathname + (search ?? "");
  useAnalyticsPageView(fullPath);
  useScrollDepth(fullPath);

  return (
    <QueryClientProvider client={queryClient}>
      <SiteHeader />
      <main className="min-h-dvh bg-background">
        <Outlet />
      </main>
      <SiteFooter />
      <DeferredChrome />
      <CookieConsent />
    </QueryClientProvider>
  );
}

